import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Receipt, ExternalLink, Check, X } from 'lucide-react';
import { getPayments, approvePayment, rejectPayment, matchPayment, naira, errText, type PaymentRow } from '@/api/admissions';
import { Card, Empty, Pill, Btn, Modal, Field, input, Notice, fmtDate } from './ui';

const VIEWS = [
  { key: 'review', label: 'Receipts to verify', params: { review: 'pending', status: 'pending' } },
  { key: 'paid', label: 'Paid', params: { status: 'successful' } },
  { key: 'pending', label: 'Started, not paid', params: { status: 'pending' } },
  { key: 'rejected', label: 'Rejected receipts', params: { review: 'rejected' } },
];

const methodLabel = (p: PaymentRow) =>
  p.gateway === 'paystack' ? 'Paystack' : p.gateway === 'manual' ? 'Receipt upload' : ['cash', 'pos'].includes(p.method) ? p.method.toUpperCase() : p.method === 'bank_deposit' ? 'Bank deposit' : 'School account';

export function PaymentsTab() {
  const qc = useQueryClient();
  const [view, setView] = useState('review');
  const [notice, setNotice] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const [rejecting, setRejecting] = useState<PaymentRow | null>(null);
  const params = VIEWS.find((v) => v.key === view)!.params;
  const q = useQuery({ queryKey: ['adm-payments', view], queryFn: () => getPayments({ ...params, per_page: 50 }) });
  const rows = q.data?.data ?? [];

  const m = useMutation({
    mutationFn: (fn: () => Promise<any>) => fn(),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['adm-payments'] }); qc.invalidateQueries({ queryKey: ['adm-summary'] }); qc.invalidateQueries({ queryKey: ['adm-apps'] }); setRejecting(null); },
    onError: (e) => setNotice({ tone: 'err', text: errText(e) }),
  });
  const run = (fn: () => Promise<any>, ok: string) => m.mutate(async () => { await fn(); setNotice({ tone: 'ok', text: ok }); });
  const total = rows.filter((r) => r.status === 'successful').reduce((s, r) => s + r.amount, 0);

  return (
    <div className="space-y-4">
      <div className="flex gap-2 overflow-x-auto pb-1">
        {VIEWS.map((v) => (
          <button key={v.key} onClick={() => setView(v.key)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border whitespace-nowrap ${view === v.key ? 'bg-[#0f172a] text-white border-[#0f172a]' : 'bg-white text-slate-700 border-slate-200'}`}>{v.label}</button>
        ))}
      </div>
      {notice && <Notice tone={notice.tone} onClose={() => setNotice(null)}>{notice.text}</Notice>}
      {view === 'paid' && rows.length > 0 && <p className="text-sm text-slate-600">Total on this page: <b>{naira(total)}</b></p>}

      <Card pad={false}>
        {q.isLoading ? <p className="p-6 text-center text-sm text-slate-500">Loading…</p> : rows.length === 0 ? (
          <Empty icon={Receipt}>{view === 'review' ? 'No receipts waiting. Uploads that match a credit on your XpressWallet are approved automatically.' : 'Nothing here.'}</Empty>
        ) : (
          <ul className="divide-y divide-slate-100">
            {rows.map((p) => (
              <li key={p.id} className="px-4 sm:px-5 py-4 flex flex-col lg:flex-row lg:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900">{p.application?.name ?? p.payer_name}</p>
                  <p className="text-xs text-slate-500">{p.application?.reference} · {p.application?.guardian_phone} · {methodLabel(p)}</p>
                  {p.proof && (
                    <p className="text-xs text-slate-600 mt-1">Ref <span className="font-mono">{p.proof.transaction_reference}</span>{p.proof.amount ? ` · paid ${naira(p.proof.amount)}` : ''}{p.proof.paid_on ? ` on ${p.proof.paid_on}` : ''}
                      {p.proof.url && <a href={p.proof.url} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-0.5 font-semibold text-green-700"><ExternalLink className="w-3 h-3" />receipt</a>}</p>
                  )}
                  {p.rejection_reason && <p className="text-xs text-red-600 mt-1">Rejected: {p.rejection_reason}</p>}
                </div>
                <div className="flex items-center gap-3 lg:text-right">
                  <div className="lg:min-w-[110px]">
                    <p className="font-bold text-slate-900">{naira(p.amount)}</p>
                    {p.ojp_charge > 0 && <p className="text-[11px] text-slate-500">+ {naira(p.ojp_charge)} charge</p>}
                    <p className="text-[11px] text-slate-500">{fmtDate(p.paid_at ?? p.proof?.submitted_at ?? p.created_at, true)}</p>
                  </div>
                  {p.status === 'successful' ? <Pill tone="green">Paid</Pill> : p.review === 'pending' ? (
                    <div className="flex gap-1.5">
                      <Btn onClick={() => run(() => approvePayment(p.id), 'Payment approved.')} disabled={m.isPending}><Check className="w-4 h-4" /><span className="hidden sm:inline">Approve</span></Btn>
                      <Btn kind="soft" onClick={() => run(() => matchPayment(p.id), 'Matched and approved.')} disabled={m.isPending} title="Look for this reference on your XpressWallet">Match</Btn>
                      <Btn kind="ghost" onClick={() => setRejecting(p)} disabled={m.isPending}><X className="w-4 h-4" /></Btn>
                    </div>
                  ) : <Pill tone="slate">{p.status}</Pill>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {rejecting && (
        <Modal title="Reject this receipt" onClose={() => setRejecting(null)}>
          <form onSubmit={(e) => { e.preventDefault(); run(() => rejectPayment(rejecting.id, String(new FormData(e.currentTarget).get('reason'))), 'Receipt rejected.'); }} className="space-y-3">
            <Field label="Tell the applicant why"><textarea name="reason" required rows={3} className={input} placeholder="e.g. We did not receive this transfer." /></Field>
            <Btn type="submit" kind="danger" disabled={m.isPending} className="w-full">Reject</Btn>
          </form>
        </Modal>
      )}
    </div>
  );
}
